import {
  withResponsiveOptions,
  withBreakpointObserver,
  getClosestParent,
} from '@studiometa/js-toolkit';
import { MenuBtn, MenuList, Menu as MenuCore } from '@studiometa/ui';

/**
 * Selector matching every element holding a menu-like component, whatever its class.
 * Used to detect nested menus (ex: the language selector) which are not instances of
 * this class and would otherwise be handled as direct children.
 */
const MENU_SELECTOR = '[data-component~="Menu"], [data-component~="LocalizationMenu"]';

/**
 * @param {*} childInstance
 * @param {*} parentInstance
 * @returns {boolean}
 */
function fixedIsDirectChild(childInstance, parentInstance) {
  if (getClosestParent(childInstance, parentInstance.constructor) !== parentInstance) {
    return false;
  }

  // The closest menu in the DOM must be the parent itself, otherwise the child
  // belongs to a nested menu of another class (ex: LocalizationMenu).
  return childInstance.$el.parentElement?.closest(MENU_SELECTOR) === parentInstance.$el;
}

/**
 * Menu class.
 */
export default class Menu extends withResponsiveOptions(withBreakpointObserver(MenuCore)) {
  static config = {
    ...MenuCore.config,
    emits: ['open', 'close'],
    options: {
      ...MenuCore.config.options,
      mode: {
        type: String,
        default: 'click',
        responsive: true,
      },
    },
    components: {
      MenuList,
      MenuBtn,
    },
  };

  /**
   * isOpen
   * @returns {boolean}
   */
  get isOpen() {
    try {
      return this.menuList?.isOpen ?? false;
    } catch {
      return false;
    }
  }

  /**
   * onMenuListItemsOpen
   * @param {*} props
   */
  onMenuListItemsOpen(props) {
    if (fixedIsDirectChild(props.target, this)) {
      super.onMenuListItemsOpen(props);
      this.$emit('open');
    }
  }

  /**
   * @param {object} props
   * @param {HTMLElement} props.target
   */
  onMenuListItemsClose({ target }) {
    if (fixedIsDirectChild(target, this)) {
      this.$emit('close');
    }
  }

  /**
   * @param {object} props
   * @param {Event} props.event
   * @param {HTMLElement} props.target
   */
  onMenuBtnClick({ target, event }) {
    if (fixedIsDirectChild(target, this) && this.shouldReactOnClick) {
      event.preventDefault();
      this.toggle();
    }
  }
}
